import { Box, Card, CardContent, CardHeader, CircularProgress, IconButton, Typography } from "@mui/material";
import { FC, useContext, useMemo } from "react";
import Center from "../common/Center";
import MapFormContext from "../../contexts/data/MapFormContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-duotone-svg-icons";
import { SpeciesContext } from "../../contexts/data/SpeciesContext";
import useGetSiteEventHistory from "../../hooks/data/useGetSiteEventHistory";
import dayjs from "dayjs";
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';

const ViewHistory: FC = () => {

    //
    // -- Context --
    const { closeDrawer, viewHistoryRoot, viewHistorySiteEventType } = useContext(MapFormContext);
    const { species: {species} } = useContext(SpeciesContext);

    // Get the site event history.
    // If viewHistoryRoot is undefined, don't send the request.
    const historyRequest = useMemo(() => {
        if (!viewHistoryRoot) {
            return undefined;
        }

        return {
            id: viewHistoryRoot,
            siteEventType: viewHistorySiteEventType,
            species: species
        };
    }, [viewHistoryRoot, viewHistorySiteEventType, species]);
    const { data: siteEventHistory, loading: siteEventHistoryLoading } = useGetSiteEventHistory(historyRequest, { revalidateOnFocus: false, errorRetryCount: 0 });

    const handleDrawerClose = () => {
        closeDrawer();
    }

    return (
        <Box
            sx={{
                position: 'relative', 
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
                justifyContent: 'stretch',
            }}
        >
            {/* -- Header -- */}
            <Box position="relative" width="100%" pt="15px" pl="15px">
                <Typography 
                    textAlign="start"
                    fontSize="1.5rem"
                    fontWeight={600}
                    sx={{ 
                        color: '#333' 
                    }}
                >
                    Site Event History
                </Typography>
            </Box>
            <Center sx={{ position: 'absolute', right: '15px', top: '15px' }}>
                <IconButton 
                    sx={{ 
                        heigh: '40px', 
                        width: '40px'
                    }}
                    onClick={handleDrawerClose}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </IconButton>
            </Center>
            
            {/** Loading */}
            {siteEventHistoryLoading && (
                <Box sx={{display: "flex", justifyContent: "center", width: "auto", padding: "10px"}}>
                    <CircularProgress />
                </Box>
            )}

            {/** Site event history */}
            {!siteEventHistoryLoading && siteEventHistory && siteEventHistory.length > 0 && (
                <Box sx={{display: "flex", flexDirection: "column", justifyContent: "center", width: "auto", padding: "10px"}}>
                    {siteEventHistory.map((historyItem, index) => (
                        <Card 
                            key={index}
                            sx={{ my: "10px" }}
                        >
                            <CardHeader
                                title={`Updated On: ${dayjs(historyItem.dateEntered).format("DD/MM/YYYY")}`}
                                subheader={`Updated by: ${historyItem.userName}`}
                                titleTypographyProps={{
                                    variant: 'subtitle1',
                                    color: 'black'
                                }}
                                subheaderTypographyProps={{
                                    variant: 'subtitle2',
                                    color: '#71797E'
                                }}
                            />
                            <CardContent>
                                { parse(DOMPurify.sanitize(historyItem.summary)) }
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            {/** Empty results message */}
            {!siteEventHistoryLoading && siteEventHistory && siteEventHistory.length === 0 && (
                <Box sx={{display: "flex", justifyContent: "center", width: "auto", padding: "10px"}}>
                    <Typography>No site event history exist for this record.</Typography>
                </Box>
            )}

        </Box>
    );
}

export default ViewHistory